import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '@viastud/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@viastud/ui/form'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { Input } from '@viastud/ui/input'
import { trpc } from '@viastud/ui/lib/trpc'
import { PhoneInput } from '@viastud/ui/phone-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viastud/ui/select'
import { OtpInput } from '@viastud/ui/shared/otp-input'
import { useSmsCaptcha } from '@viastud/ui/shared/sms-captcha'
import { grade, userDetailsSchema } from '@viastud/utils'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { OnboardingCard } from '@/components/onboarding/onboarding-card'
import { useAuthStore } from '@/store/auth.store'

const searchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute('/auth/success')({
  validateSearch: (search) => searchSchema.parse(search),
  component: AuthSuccess,
})

// Définition du schema pour le formulaire
const updateUserSchema = z.object({
  firstName: z.string().min(1, 'Le prénom est requis'),
  lastName: z.string().min(1, 'Le nom est requis'),
  phoneNumber: z.string().min(10, 'Le téléphone est requis'),
  grade: z.enum(grade),
})

type UpdateUserForm = z.infer<typeof updateUserSchema>

function AuthSuccess() {
  const { requestToken, captchaDialog } = useSmsCaptcha()
  const navigate = useNavigate()
  const { token } = Route.useSearch()
  const { toast } = useToast()
  const { updateAuth, user: userAuthenticated } = useAuthStore()

  const { mutateAsync: signInMutation } = trpc.userAuth.signInWithToken.useMutation()
  const { mutateAsync: updateUserMutation } = trpc.user.updateUserDetails.useMutation()
  const sendPhoneVerificationCode = trpc.userAuth.sendPhoneVerificationCode.useMutation()
  const verifyPhoneCode = trpc.userAuth.verifyPhoneCode.useMutation()

  const [showLoader, setShowLoader] = useState(true)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isOtpStep, setIsOtpStep] = useState(false)
  const [otpCode, setOtpCode] = useState('')

  const logout = useCallback(() => {
    updateAuth({
      user: undefined,
      role: null,
      isAuthenticated: false,
    })
    void navigate({ to: '/login' })
  }, [navigate, updateAuth])

  // Connexion via token
  const loginWithToken = useCallback(async () => {
    setShowLoader(true)
    if (!token) {
      toast({ description: 'Token invalide.', variant: 'destructive' })
      logout()
      return
    }

    try {
      const { user, role, isFinished } = await signInMutation({ token })
      updateAuth({
        user,
        role,
        isAuthenticated: true,
      })
      if (isFinished) {
        void navigate({ to: '/' })
      }
    } catch {
      toast({ description: 'Erreur lors de la connexion.', variant: 'destructive' })
      logout()
    } finally {
      setShowLoader(false)
    }
  }, [logout, navigate, signInMutation, toast, token, updateAuth])
  const sendSMSCode = useCallback(
    async (phoneNumber: string) => {
      try {
        await sendPhoneVerificationCode.mutateAsync({
          phoneNumber,
          captchaToken: await requestToken(),
        })
        toast({ title: 'Code de vérification envoyé' })
        setIsOtpStep(true)
      } catch (error) {
        toast({
          description:
            error instanceof Error
              ? error.message
              : "Erreur lors de l'envoi du code de vérification.",
          variant: 'destructive',
        })
      }
    },
    [sendPhoneVerificationCode, toast, requestToken]
  )

  useEffect(() => {
    void loginWithToken()
  }, [loginWithToken])

  const form = useForm<UpdateUserForm>({
    mode: 'onChange',
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      firstName: userAuthenticated?.firstName ?? '',
      lastName: userAuthenticated?.lastName ?? '',
      phoneNumber: '',
      grade: 'TROISIEME',
    },
  })

  const onSubmit = async (data: UpdateUserForm) => {
    if (!isOtpStep) {
      void sendSMSCode(data.phoneNumber)
      return
    }

    try {
      await verifyPhoneCode.mutateAsync({
        phoneNumber: form.getValues('phoneNumber'),
        code: otpCode,
      })
    } catch (error) {
      toast({ description: error instanceof Error ? error.message : 'Err', variant: 'destructive' })
      return
    }

    if (!userAuthenticated) {
      logout()
      return
    }

    try {
      await updateUserMutation(
        userDetailsSchema.parse({
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber,
          grade: data.grade,
          interestedIn: ['MATHS'],
          doneModules: [],
          doingModules: [],
          isFinished: true,
        })
      )
      toast({ title: 'Profil mis à jour avec succès' })
      setFormSubmitted(true)
      updateAuth({
        user: {
          ...userAuthenticated,
          phoneNumber: data.phoneNumber,
          grade: data.grade,
          createdAt: userAuthenticated?.createdAt ?? new Date().toISOString(),
        },
        role: 'STUDENT',
        isAuthenticated: true,
      })
      void navigate({ to: '/' })
    } catch {
      toast({ description: 'Erreur lors de la mise à jour.', variant: 'destructive' })
    }
  }

  if (showLoader) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
          <p className="text-gray-600">Finalisation de la connexion...</p>
        </div>
      </div>
    )
  }

  if (formSubmitted) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <p className="text-gray-600">Votre profil est complet ! Redirection...</p>
      </div>
    )
  }

  return (
    <div className="relative flex h-screen w-screen flex-col items-center justify-center gap-y-3">
      <OnboardingCard>
        {captchaDialog}
        <h1 className="mb-4 text-2xl font-bold text-gray-900">Complétez vos informations</h1>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex w-full max-w-md flex-col gap-4"
          >
            <FormField
              name="firstName"
              control={form.control}
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <FormLabel className="text-sm font-medium text-gray-700">Prénom</FormLabel>
                  <FormControl>
                    <Input className="w-full" placeholder="John" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="lastName"
              control={form.control}
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <FormLabel className="text-sm font-medium text-gray-700">Nom</FormLabel>
                  <FormControl>
                    <Input className="w-full" placeholder="Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="grade"
              rules={{ required: 'Le niveau est obligatoire' }}
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <FormLabel className="text-sm font-medium text-gray-700">Niveau</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sélectionner un niveau" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TROISIEME">Troisième</SelectItem>
                        <SelectItem value="SECONDE">Seconde</SelectItem>
                        <SelectItem value="PREMIERE">Première</SelectItem>
                        <SelectItem value="TERMINALE">Terminale</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="phoneNumber"
              control={form.control}
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <FormLabel className="text-sm font-medium text-gray-700">Téléphone</FormLabel>

                  <FormControl>
                    <PhoneInput
                      className="w-full"
                      placeholder="Numéro de téléphone"
                      {...field}
                      disabled={isOtpStep}
                      onChange={(value: string) => {
                        field.onChange(value)
                      }}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            {isOtpStep && (
              <div className="flex w-full flex-col gap-2 rounded-lg border border-indigo-100 bg-indigo-50/30 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Confirmer le numéro</p>
                  <button
                    type="button"
                    className="text-xs text-indigo-600 underline"
                    onClick={() => {
                      setIsOtpStep(false)
                    }}
                  >
                    Modifier le numéro
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Entrez le code envoyé au {form.getValues('phoneNumber')}
                </p>
                <OtpInput
                  value={otpCode}
                  onChange={setOtpCode}
                  onResendOtp={() => sendSMSCode(form.getValues('phoneNumber'))}
                />
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              className="mt-4 w-full"
              disabled={isOtpStep && otpCode.length !== 6}
            >
              {isOtpStep ? 'Vérifier et Mettre à jour' : 'Envoyer le code'}
            </Button>
          </form>
        </Form>
      </OnboardingCard>

      <Button onClick={logout} variant="ghost">
        Se déconnecter
      </Button>
    </div>
  )
}
